/**
 * Ant Design 主题配置文件
 *
 * 官方文档: https://ant.design/docs/react/customize-theme-cn
 *
 * 此文件集中管理了应用的所有样式配置，迁移自 App.js 和 index.css。
 * 您可以在此修改全局和组件级别的设计 Token。
 */

// 定义品牌色板 (微信绿主题)
const TuiwaiColors = {
  // --- 品牌色 ---
  primary: '#1AAD19',        // 主色，用于链接、选中项、主要按钮等
  primaryHover: '#2BA245',   // 主色悬停状态
  primaryActive: '#179b16',  // 主色激活状态 (稍暗的绿色)
  primaryBg: '#F1F1F1',      // 选中项的浅色背景

  // --- 功能色 ---
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
  info: '#1677ff',

  // --- 中性色 (来自新色板) ---
  textBase: '#4D4D4D',          // 基础文本颜色
  textSecondary: '#888888',     // 次要文本
  textTertiary: '#AAAAAA',      // 第三级文本/禁用文字
  border: '#AAAAAA',            // 边框颜色
  divider: '#F1F1F1',           // 分割线颜色
  background: '#F1F1F1',        // 页面或组件背景色
  white: '#ffffff',

  // --- 按钮特定颜色 ---
  buttonDefault: '#4D4D4D',
  buttonDefaultHover: '#888888',

  // --- 禁用状态 ---
  disabledBg: '#AAAAAA',
  disabledText: '#ffffff',

  // --- 表格 ---
  tableSelectedBg: '#F1F1F1',
  tableSelectedHoverBg: '#e8f7e8', // 浅绿色悬停
};

// --- 导航菜单 (浅色主题) ---
// 在对象创建后添加依赖性属性，以避免初始化错误
TuiwaiColors.siderBg = TuiwaiColors.background; // Sider 使用浅灰色背景
TuiwaiColors.menuBg = TuiwaiColors.white; // Menu 使用纯白背景，形成卡片效果
TuiwaiColors.menuItemColor = TuiwaiColors.textBase;
TuiwaiColors.menuItemHoverColor = TuiwaiColors.primary;
TuiwaiColors.menuItemSelectedColor = TuiwaiColors.primary;
TuiwaiColors.menuItemSelectedBg = TuiwaiColors.primaryBg;


const theme = {
  // ===================================================================
  // 全局 Token 定义
  // ===================================================================
  token: {
    // --- 颜色 ---
    colorPrimary: TuiwaiColors.primary,
    colorPrimaryHover: TuiwaiColors.primaryHover,
    colorPrimaryActive: TuiwaiColors.primaryActive,
    colorPrimaryBg: TuiwaiColors.primaryBg,
    colorSuccess: TuiwaiColors.success,
    colorWarning: TuiwaiColors.warning,
    colorError: TuiwaiColors.error,
    colorInfo: TuiwaiColors.info,
    colorTextBase: TuiwaiColors.textBase,
    colorBgLayout: TuiwaiColors.background,
    colorWhite: TuiwaiColors.white,

    // --- 自定义 Token ---
    siderBg: TuiwaiColors.siderBg, // 将Sider背景色提升为全局Token

    // --- 字体 (来自 index.css) ---
    fontFamily: `'Alibaba PuHuiTi', -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif`,
    fontSize: 14,
    lineHeight: 22 / 14, // 目标行高 22px / 基础字号 14px

    // --- 尺寸 & 边距 ---
    borderRadius: 2,
  },

  // ===================================================================
  // 组件级 Token 定义
  // ===================================================================
  components: {
    Layout: {
      sider: {
        colorBgSider: TuiwaiColors.siderBg,
        colorBgContainer: TuiwaiColors.siderBg, // 强制覆盖容器背景色
      },
      header: {
        colorBgHeader: TuiwaiColors.white,
        height: 64,
      },
    },
    Menu: {
      colorItemBg: TuiwaiColors.menuBg,
      colorSubItemBg: TuiwaiColors.menuBg,
      colorItemText: TuiwaiColors.menuItemColor,
      colorItemTextHover: TuiwaiColors.menuItemHoverColor,
      colorItemTextSelected: TuiwaiColors.menuItemSelectedColor,
      colorItemBgSelected: TuiwaiColors.menuItemSelectedBg,
    },
    Button: {
      // --- 主按钮 (Primary) 使用品牌色 ---
      colorPrimary: TuiwaiColors.primary,
      colorPrimaryHover: TuiwaiColors.primaryHover,
      colorPrimaryActive: TuiwaiColors.primaryActive,
      
      // --- 默认按钮 (Default) 使用灰色系 ---
      defaultColor: TuiwaiColors.buttonDefault,
      defaultBorderColor: TuiwaiColors.buttonDefault,
      defaultHoverColor: TuiwaiColors.buttonDefaultHover,
      defaultHoverBorderColor: TuiwaiColors.buttonDefaultHover,
      
      // --- 其他 ---
      defaultBg: 'transparent',
      linkHoverBg: 'transparent',
      colorTextDisabled: TuiwaiColors.disabledText,
      colorBgContainerDisabled: TuiwaiColors.disabledBg,
    },
    Table: {
      // 表格行选中使用浅绿色
      rowSelectedBg: TuiwaiColors.tableSelectedBg,
      rowSelectedHoverBg: TuiwaiColors.tableSelectedHoverBg,
    },
    Checkbox: {
      // 复选框使用品牌色
      colorPrimary: TuiwaiColors.primary,
      colorPrimaryHover: TuiwaiColors.primaryHover,
    },
    Radio: {
      // 单选框使用品牌色
      colorPrimary: TuiwaiColors.primary,
      colorPrimaryHover: TuiwaiColors.primaryHover,
    },
    Switch: {
      // 开关使用品牌色
      colorPrimary: TuiwaiColors.primary,
      colorPrimaryHover: TuiwaiColors.primaryHover,
    }
  },
};

export default theme;